import type { CurrentUser } from '@platform/types';
import { create } from 'zustand';

import {
  apiFetch,
  setAccessToken,
  setActiveOrganizationId,
} from '@/lib/api-client';

interface AuthState {
  user: CurrentUser | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
  error: string | null;
}

interface AuthActions {
  bootstrap: () => Promise<void>;
  login: (input: { email: string; password: string }) => Promise<{ verificationRequired?: boolean }>;
  logout: () => Promise<void>;
  setActiveOrg: (organizationId: string) => Promise<void>;
  clear: () => void;
}

const initial: AuthState = {
  user: null,
  status: 'idle',
  error: null,
};

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  ...initial,

  bootstrap: async () => {
    set({ status: 'loading' });
    try {
      const user = await apiFetch<CurrentUser>('/auth/me');
      setActiveOrganizationId(user.activeOrganizationId);
      set({ user, status: 'authenticated', error: null });
    } catch {
      set({ user: null, status: 'unauthenticated' });
    }
  },

  login: async (input) => {
    set({ status: 'loading', error: null });
    try {
      const result = await apiFetch<{ accessToken: string; user: CurrentUser }>(
        '/auth/login',
        { method: 'POST', body: input },
      );
      setAccessToken(result.accessToken);
      setActiveOrganizationId(result.user.activeOrganizationId);
      set({ user: result.user, status: 'authenticated', error: null });
      return {};
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ status: 'unauthenticated', error: message });
      throw err;
    }
  },

  logout: async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {
      // proceed
    }
    get().clear();
  },

  setActiveOrg: async (organizationId) => {
    const user = await apiFetch<CurrentUser>(`/auth/switch-org/${organizationId}`, {
      method: 'POST',
    });
    setActiveOrganizationId(user.activeOrganizationId);
    set({ user });
  },

  clear: () => {
    setAccessToken(null);
    setActiveOrganizationId(null);
    set({ user: null, status: 'unauthenticated', error: null });
  },
}));
